import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Badge,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalOffer as DiscountIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useApiCart } from '../hooks/useApiCartProvider';
import { customerService } from '../services';
import { computeBreakdown } from '../utils/pricing';

interface CartSummaryProps {
  onCheckout?: () => void;
  compact?: boolean;
}

interface CartTotals {
  /** Total Selling Price — pre-discount subtotal. */
  sellingPrice: number;
  /** Discount applied. */
  totalDiscount: number;
  /** After discount, before tax. */
  subtotal: number;
  /** PPN (11% of subtotal). */
  tax: number;
  /** Grand total = subtotal + tax. */
  total: number;
  hasDiscounts: boolean;
}

export const CartSummaryWithDiscounts: React.FC<CartSummaryProps> = ({ 
  onCheckout, 
  compact = false 
}) => {
  const { t } = useTranslation();
  const {
    cartItems,
    totalItems,
    isLoading,
    error,
    updateCartItem,
    removeFromCart,
    clearCart,
    clearError,
  } = useApiCart();

  // Calculate cart totals including discount and PPN (11%).
  const calculateTotals = (): CartTotals => {
    const sellingPrice = cartItems.reduce((sum, item) =>
      sum + (item.originalTotalPrice || item.total_price), 0
    );
    const totalDiscount = cartItems.reduce((sum, item) =>
      sum + (item.totalDiscountAmount || 0), 0
    );
    const breakdown = computeBreakdown(sellingPrice, totalDiscount);
    return {
      sellingPrice: breakdown.originalSubtotal,
      totalDiscount: breakdown.discount,
      subtotal: breakdown.subtotal,
      tax: breakdown.tax,
      total: breakdown.total,
      hasDiscounts: totalDiscount > 0,
    };
  };

  const totals = calculateTotals();

  const handleQuantityChange = async (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      await removeFromCart(productId);
    } else {
      await updateCartItem(productId, newQuantity);
    }
  };

  const handleRemoveItem = async (productId: string) => {
    await removeFromCart(productId);
  };

  const handleClearCart = async () => {
    if (window.confirm(t('cart.confirmClear'))) {
      await clearCart();
    }
  };

  const formatDiscountSource = (source?: string): string => {
    if (!source) return '';
    
    if (source.startsWith('part:')) {
      return `Part discount: ${source.substring(5)}`;
    } else if (source.startsWith('product_code:')) {
      return `Product discount: ${source.substring(13)}`;
    }
    return source;
  };

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Badge badgeContent={totalItems} color="primary">
          <ShoppingCartIcon />
        </Badge>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {totals.hasDiscounts && (
            <Typography variant="caption" color="success.main" sx={{ lineHeight: 1 }}>
              Save {customerService.formatCurrency(totals.totalDiscount)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {customerService.formatCurrency(totals.total)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Shopping Cart ({totalItems} items)
          </Typography>
          {cartItems.length > 0 && (
            <Button
              size="small"
              color="error"
              onClick={handleClearCart}
              disabled={isLoading}
            >
              {t('cart.clearCart')}
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {totals.hasDiscounts && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<DiscountIcon />}>
            You're saving {customerService.formatCurrency(totals.totalDiscount)} with applied discounts!
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {cartItems.length === 0 && !isLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <ShoppingCartIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('cart.empty')}
            </Typography>
            <Button
              variant="outlined"
              href="/products"
              sx={{ mt: 1, textTransform: 'none' }}
            >
              {t('cart.continueShopping')}
            </Button>
          </Box>
        ) : (
          <>
            <List>
              {cartItems.map((item) => (
                <React.Fragment key={item.id}>
                  <ListItem sx={{ px: 0, pb: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.product_name}
                          {item.hasDiscount && (
                            <Tooltip title={formatDiscountSource(item.discountSource)}>
                              <Chip
                                label={`${item.discountPercent?.toFixed(1)}% OFF`}
                                size="small"
                                color="success"
                                icon={<DiscountIcon />}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.product_description}
                          </Typography>
                          
                          {/* Pricing information with discount details */}
                          <Box sx={{ mt: 1 }}>
                            {item.hasDiscount ? (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      textDecoration: 'line-through',
                                      color: 'text.disabled'
                                    }}
                                  >
                                    {customerService.formatCurrency(item.unit_price)} each
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ fontWeight: 'medium', color: 'success.main' }}
                                  >
                                    {customerService.formatCurrency(item.discountedUnitPrice || 0)} each
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="success.main">
                                  Save {customerService.formatCurrency(item.discountAmount || 0)} per unit
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {customerService.formatCurrency(item.unit_price)} each
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    
                    {/* Quantity controls and total */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                      {/* Quantity controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                          disabled={isLoading}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          size="small"
                          value={item.quantity}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= 0) {
                              handleQuantityChange(item.product_id, value);
                            }
                          }}
                          inputProps={{
                            min: 1,
                            type: 'number',
                            style: { width: '60px', textAlign: 'center' }
                          }}
                          disabled={isLoading}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                          disabled={isLoading}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      
                      {/* Line total with discount breakdown */}
                      <Box sx={{ textAlign: 'right' }}>
                        {item.hasDiscount ? (
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                textDecoration: 'line-through',
                                color: 'text.disabled'
                              }}
                            >
                              {customerService.formatCurrency(item.originalTotalPrice || 0)}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              {customerService.formatCurrency(item.total_price)}
                            </Typography>
                            <Typography variant="caption" color="success.main">
                              Save {customerService.formatCurrency(item.totalDiscountAmount || 0)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {customerService.formatCurrency(item.total_price)}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Remove button */}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(item.product_id)}
                        disabled={isLoading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>

            {/* Cart totals section — matches invoice summary layout:
                Total Selling Price / Discount / Subtotal / PPN / Total */}
            <Box sx={{ mt: 2, pt: 2, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Total Selling Price (pre-discount subtotal) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" color="text.secondary">{t('cart.totalSellingPrice')}</Typography>
                  <Typography variant="body1">
                    {customerService.formatCurrency(totals.sellingPrice)}
                  </Typography>
                </Box>

                {/* Discount (always shown so layout matches the invoice even at 0) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body1"
                    color={totals.hasDiscounts ? 'success.main' : 'text.secondary'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {totals.hasDiscounts && <DiscountIcon fontSize="small" />}
                    Discount (Rp)
                  </Typography>
                  <Typography
                    variant="body1"
                    color={totals.hasDiscounts ? 'success.main' : 'text.secondary'}
                  >
                    {totals.hasDiscounts
                      ? `- ${customerService.formatCurrency(totals.totalDiscount)}`
                      : customerService.formatCurrency(0)}
                  </Typography>
                </Box>

                {/* Subtotal (after discount, before tax) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body1">
                    {customerService.formatCurrency(totals.subtotal)}
                  </Typography>
                </Box>

                {/* PPN line intentionally hidden — Mowilex's price list
                    is VAT-inclusive, so the subtotal already represents
                    the customer-facing total. The DMS invoice from Zegen
                    shows the PPN portion broken out for faktur pajak. */}

                <Divider />

                {/* Grand total */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {customerService.formatCurrency(totals.total)}
                  </Typography>
                </Box>

                {/* Savings message */}
                {totals.hasDiscounts && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="body2" color="success.main">
                      🎉 You saved {customerService.formatCurrency(totals.totalDiscount)} on this order!
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {onCheckout && (
                <Button
                  variant="contained"
                  size="medium"
                  onClick={onCheckout}
                  disabled={cartItems.length === 0 || isLoading}
                  sx={{ mt: 2, px: 4, alignSelf: 'center' }}
                >
                  {t('cart.proceedToCheckout')}
                </Button>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CartSummaryWithDiscounts;